<script setup lang="ts">
import type { ITableColumnsOptions } from '@modules/tables/types';
import VCard from '@components/card/VCard/VCard.vue';
import {
  AFFILIATE_PROGRAM_TRACKERS_DEAL_SETTINGS_MAIN_ROUTE_NAME,
  AFFILIATE_PROGRAM_TRACKERS_FIXED_PAYMENTS_ROUTE_NAME,
  AFFILIATE_PROGRAM_TRACKERS_LIST_QUERY_KEY,
  AFFILIATE_PROGRAM_TRACKERS_PERMISSIONS,
  TRANSLATION_GROUP_AFFILIATE_PROGRAM_TRACKERS,
} from '@modules/affiliate-program-trackers/constants';
import { useDownloadAffiliateProgramTrackersReportMutation } from '@modules/affiliate-program-trackers/mutations';
import { useAffiliateProgramTrackersListQuery } from '@modules/affiliate-program-trackers/queries';
import VChangeTrafficSource from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/components/VChangeTrafficSource/VChangeTrafficSource.vue';
import VCreateEditTracker from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/components/VCreateEditTracker/VCreateEditTracker.vue';
import VEditTrackerCountry from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/components/VEditTrackerCountry/VEditTrackerCountry.vue';
import { useTrackerTableActions } from '@modules/affiliates-approved/submodules/trackers-settings/submodules/trackers-list/composables';
import VDownloadReportButton
  from '@modules/download-report/components/VDownloadReportButton/VDownloadReportButton.vue';
import { useDownloadReport } from '@modules/download-report/composables';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import VTableMultiselectActions from '@modules/tables/components/VTableMultiselectActions/VTableMultiselectActions.vue';
import { useTableData } from '@modules/tables/composables';
import { versionSplitterTags } from '@modules/tables/utils/formatters';

const {
  tableState,
  isLoading,
  isFetching,
  data,
  updateTableState,
  refreshTableData,
} = useTableData(useAffiliateProgramTrackersListQuery);

const {
  showCreateEditTrackerDialog,
  showEditCountryDialog,
  showChangeTrafficSourceDialog,
  affiliateId,
  trackerId,
  trackerDescription,
  trafficSourceId,
  tableActions,
  onTableActionItemSelect,
  onHideCreateEditDialog,
} = useTrackerTableActions(
  data,
  AFFILIATE_PROGRAM_TRACKERS_DEAL_SETTINGS_MAIN_ROUTE_NAME,
  AFFILIATE_PROGRAM_TRACKERS_FIXED_PAYMENTS_ROUTE_NAME,
);

const columnOptions: ITableColumnsOptions = {
  aasm_state: {
    localization: {
      translationPath: 'admin.partnership_program.trackers.aasm_state.',
    },
  },
  tracker_code: {
    defaultMobilePreview: true,
  },
  type_of_transaction: {
    localization: {
      translationPath: 'admin.partnership_program.conditions.create_edit.main_params.form.',
    },
  },
  hotc_condition_version: {
    formatValue: versionSplitterTags,
  },
};

const {
  isReportLoading,
  onDownloadReport,
} = useDownloadReport(useDownloadAffiliateProgramTrackersReportMutation(), tableState);
</script>

<template>
  <VCard v-data-qa-auto-id="'AffiliateProgramTrackersListPage'" type="table">
    <VTable
      :data="data"
      :state="tableState"
      :loading="isLoading"
      :is-fetching="isFetching"
      :title-t-path="$getTPath(TRANSLATION_GROUP_AFFILIATE_PROGRAM_TRACKERS, 'HEADER')"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VTableMultiselectActions
          :list-item="listItem"
          :options="tableActions"
          :disabled="false"
          static
          @select="onTableActionItemSelect"
        />
      </template>
      <template
        v-if="$hasPermission(AFFILIATE_PROGRAM_TRACKERS_PERMISSIONS.DOWNLOAD_LIST)"
        #footerButtons
      >
        <VDownloadReportButton
          :label="$getTranslation(TRANSLATION_GROUP_AFFILIATE_PROGRAM_TRACKERS, 'DOWNLOAD_REPORT')"
          :loading="isReportLoading"
          @download="onDownloadReport"
        />
      </template>
    </VTable>
  </VCard>

  <VCreateEditTracker
    v-if="affiliateId"
    v-model:visible="showCreateEditTrackerDialog"
    :aff-id="affiliateId"
    :tracker-id="trackerId"
    :tracker-description="trackerDescription"
    :trackers-list-query-key="AFFILIATE_PROGRAM_TRACKERS_LIST_QUERY_KEY"
    @hide="onHideCreateEditDialog"
  />

  <VEditTrackerCountry
    v-if="affiliateId"
    v-model:visible="showEditCountryDialog"
    :aff-id="affiliateId"
    :tracker-id="trackerId"
    :trackers-list-query-key="AFFILIATE_PROGRAM_TRACKERS_LIST_QUERY_KEY"
    @hide="onHideCreateEditDialog"
  />

  <VChangeTrafficSource
    v-if="affiliateId"
    v-model:visible="showChangeTrafficSourceDialog"
    :aff-id="affiliateId"
    :tracker-id="trackerId"
    :traffic-source-id="trafficSourceId"
    :trackers-list-query-key="AFFILIATE_PROGRAM_TRACKERS_LIST_QUERY_KEY"
    @hide="onHideCreateEditDialog"
  />

  <RouterView />
</template>
